import { isAbortOrCanceledError } from "./abort-signals";
import { sleep } from "./sleep";

export class RetryError extends Error {
  name = "RetryError";
  attempts: number;
  lastError: unknown;
  durationMs: number;

  constructor(props: { attempts: number; lastError: unknown; durationMs: number }) {
    const message = `Failed after ${props.attempts} attempts: ${getErrorMessage(props.lastError)}`;
    super(message, { cause: props.lastError });

    this.attempts = props.attempts;
    this.lastError = props.lastError;
    this.durationMs = props.durationMs;
    this.stack = props.lastError instanceof Error ? props.lastError.stack : this.stack;
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "unknown error";
}

interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  retryOnCancel?: boolean;
}

const DEFAULT_RETRY_OPTIONS = {
  maxAttempts: 10,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  retryOnCancel: false,
};

function calculateDelay(
  attempt: number,
  options: Required<Pick<RetryOptions, "initialDelayMs" | "maxDelayMs" | "backoffMultiplier">>
) {
  const exponentialDelay =
    options.initialDelayMs * Math.pow(options.backoffMultiplier, attempt - 1);
  return Math.min(exponentialDelay, options.maxDelayMs);
}

export async function withRetry<R>(fn: () => Promise<R>, options: RetryOptions = {}): Promise<R> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
  const start = performance.now();
  let lastError: unknown;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (isAbortOrCanceledError(error) && !config.retryOnCancel) {
        throw error;
      }

      if (config.shouldRetry && !config.shouldRetry(error, attempt)) {
        throw error;
      }

      if (attempt === config.maxAttempts) {
        const durationMs = Math.ceil(performance.now() - start);
        throw new RetryError({ attempts: attempt, lastError: error, durationMs });
      }

      const delayMs = calculateDelay(attempt, config);
      await sleep(delayMs);
    }
  }

  const durationMs = Math.ceil(performance.now() - start);
  throw new RetryError({ attempts: config.maxAttempts, lastError, durationMs });
}
